import { camelCase, cloneDeep } from "lodash";
import Vue from "vue";

const catalog = {
  namespaced: true,

  state: () => ({
    catalogsData: [],
    projectCatalogs: [],
    projectProducts: [],
  }),
  getters: {
    catalogsList: (state) => {
      const catalogsNames = state.catalogsData?.map(
        (item) => item?.catalogName
      );

      const list = [...new Set(catalogsNames)]?.filter((item) => !!item);

      return list.map((catalogName) => {
        const type = state.catalogsData?.find(
          (item) => item?.catalogName === catalogName
        )?.catalogType;

        return {
          name: catalogName,
          type: type ? type.toLowerCase() : type,
        };
      });
    },

    normalizeProduct: () => (pr) => {
      return {
        ...pr,

        catalogName: pr.catalog_name,
        catalogType: pr.catalog_type,
        catalogPageOrder: pr.catalog_page_order,
        customCatalogId: pr.custom_catalog_id,

        dealerPrice: pr.dealer_price || pr.dealerPrice,
        order: pr.order,
        price: Number(pr.price || pr.msrp).toFixed(2),
        sizes: pr?.sizes,
        styleId: pr.styleId || pr.style_id,
        related: pr.related,
        wrapperId: pr.wrapperId || pr.product?.wrapperId || pr?.id,

        //Is or not sublimable prop
        //Is uniform
        isUniform: pr.category === "Jerseys",

        //Custom Configs
        threekitConfigId: pr.threekit_config_id,
        threekitSavedPreview: pr.threekit_saved_preview,
        threekitDecorations: pr.threekit_decorations,
      };
    },

    getProductCatalogData: (state) => (styleId) => {
      return state.catalogsData?.find(
        (catalogProduct) => catalogProduct?.style === styleId
      );
    },

    getProjectProductById: (state) => (productId) => {
      return state.projectProducts?.find((pr) => pr?.id === productId);
    },

    getNormalizedBrand: () => (brandName) => {
      return brandName?.toLowerCase()?.replace(/\s+/g, "-");
    },

    getProductBrandIcon: (_, getters) => (brandName) => {
      return `/assets/branding/${getters.getNormalizedBrand(brandName)}.png`;
    },

    productBrandingIconIsValid: (_, getters) => (brandName) => {
      const brand = getters.getNormalizedBrand(brandName);

      return (
        brand === "chromagear" ||
        brand === "badger" ||
        brand === "alleson" ||
        brand === "c2" ||
        brand === "badger-chromagear"
      );
    },
  },
  actions: {
    async getProductsCatalogs({ dispatch, commit }) {
      try {
        const datatableId = "61782339-6474-461d-8d99-7e1a42c7650a";
        const data = await dispatch("threekit/getDataTable", datatableId, {
          root: true,
        });

        const normalizedData = data?.rows?.map((row) => {
          return (
            row?.value &&
            Object.keys(row?.value)?.reduce((acc, key) => {
              return {
                ...acc,
                [camelCase(key)]: row?.value?.[key],
              };
            }, {})
          );
        });

        commit("SET_CATALOGS_DATA", normalizedData);
      } catch (error) {
        console.log(error);
      }
    },

    async setProjectCatalogs({ getters, commit }, catalogs) {
      const catalogsData = catalogs?.map((catalog) => {
        return getters.catalogsList?.find(
          (catalogInList) => catalogInList?.name?.toLowerCase() === catalog
        );
      });
      commit("SET_PROJECT_CATALOGS", catalogsData);
    },

    async setCatalogProducts({ getters, commit }, products) {
      commit(
        "SET_PROJECT_PRODUCTS",
        products.map((pr) => getters["normalizeProduct"](pr))
      );
    },

    async setProjectProduct({ state, getters, commit }, product) {
      const normalizedProduct = getters["normalizeProduct"](product);

      try {
        const prIndex = state.projectProducts.findIndex(
          (pr) => pr.id === normalizedProduct.id
        );

        commit("SET_PROJECT_PRODUCT", { prIndex, product: normalizedProduct });
      } catch (error) {
        console.log(error);
      }
    },

    async removeProjectProduct({ commit }, prId) {
      try {
        commit("REMOVE_PROJECT_PRODUCT", prId);
      } catch (error) {
        console.log(error);
      }
    },

    //Products
    async updateProductsThreekitData({ state, dispatch }, products) {
      //Iterate over each column of each page

      //First, load Styles
      await Promise.all(
        products?.map(async (pr) => {
          return await dispatch(
            "threekit/initProductCache",
            {
              styleName: pr.styleId?.toUpperCase(),
              designName: "",
            },
            { root: true }
          );
        })
      );

      //Then, load designs
      // await Promise.all(
      //   products?.map(async (pr) => {
      //     return await dispatch(
      //       "threekit/initProductCache",
      //       {
      //         styleName: pr.styleId?.toUpperCase(),
      //         designName: '',
      //       },
      //       { root: true }
      //     );
      //   })
      // );

      products?.map(async (pr) => {
        const productDefaultData = state.catalogsData?.find(
          (prData) =>
            prData?.style && pr?.styleId && prData?.style === pr?.styleId
        );
        const productIsInCatalogData = productDefaultData ? true : false;

        //If the product is not in the default catalog data, it means it has been changed manually or by the gender filter
        const designs = productIsInCatalogData
          ? Object.keys(productDefaultData)
              ?.filter(
                (key) => key?.includes("design") || key?.includes("hero")
              )
              .map((d) => productDefaultData?.[d])
          : pr?.threekit_designs?.map((design) => design?.design_name);

        const designsToFetch = productIsInCatalogData
          ? designs
          : designs?.slice(0, 7);

        
        designsToFetch &&
          designsToFetch.length &&
          designsToFetch.forEach(async (design) => {
            design &&
              (await dispatch(
                "threekit/initProductCache",
                {
                  styleName: pr.styleId?.toUpperCase(),
                  designName: design,
                },
                { root: true }
              ));
          });
      });
    },

    async updateProjectProduct({ getters, commit }, payload) {
      try {
        const product = getters["getProjectProductById"](
          payload.custom_catalog_product_id
        );

        commit("UPDATE_PROJECT_PRODUCT", {
          ...product,
          related: payload.related,
        });
      } catch (error) {
        console.log(error);
      }
    },
  },

  mutations: {
    SET_CATALOGS_DATA: (state, data) => (state.catalogsData = data),
    SET_PROJECT_CATALOGS: (state, catalogs) => {
      state.projectCatalogs = catalogs;
    },
    SET_PROJECT_PRODUCTS: (state, products) =>
      (state.projectProducts = products),

    SET_PROJECT_PRODUCT: (state, { prIndex, product }) => {
      Vue.set(state.projectProducts, prIndex, product);
    },

    REMOVE_PROJECT_PRODUCT: (state, prId) => {
      const filteredProducts = state.projectProducts?.filter(
        (pr) => pr.id !== prId
      );

      state.projectProducts = filteredProducts;
    },

    UPDATE_PROJECT_PRODUCT: (state, updateProduct) => {
      const updatedProducts = state.projectProducts.map((pr) => {
        if (pr.id === updateProduct.id) {
          return cloneDeep(updateProduct);
        } else {
          return cloneDeep(pr);
        }
      });

      state.projectProducts = updatedProducts;
    },
  },
};

export default catalog;
